import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { storeAuth } from '../api/client';

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const InnerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let storeInitDone = false;
    // Guard against React StrictMode double-invocation in dev
    const hasRunKey = 'auth_me_called_once';
    if (import.meta.env.DEV && (window as any)[hasRunKey]) {
      mounted = false;
      setIsLoading(false);
      return;
    }
    (window as any)[hasRunKey] = true;
    (async () => {
      try {
        // For local development, use environment variable token if available
        if (import.meta.env.VITE_USER_TOKEN) {
          // Local development with provided token
          if (mounted) setIsAuthenticated(true);

          // Initialize store authentication with the provided token
          try {
            const path = window.location.pathname || '';
            if (path !== '/login' && storeAuth.shouldRefreshStoreBearer() && !storeInitDone) {
              storeInitDone = true;
              await storeAuth.refreshStoreBearer();
            }
          } catch (_) {
            // ignore store auth failures here; UI can still function without store calls
          }
        } else {
          // Production/deployed environment - use relative path for auth check to stay on collector domain
          // The ingress routes /auth/* to the scraper backend
          const response = await fetch('/auth/me', {
            method: 'GET',
            credentials: 'include',
          });

          if (response.ok) {
            if (mounted) setIsAuthenticated(true);

            // After auth, fetch Store user token using Azure id_token cookie
            try {
              // Do not attempt Store token exchange when on /login route to avoid loops
              const path = window.location.pathname || '';
              if (path !== '/login' && storeAuth.shouldRefreshStoreBearer() && !storeInitDone) {
                storeInitDone = true;
                await storeAuth.refreshStoreBearer();
              }
            } catch (_) {
              // ignore store auth failures here; UI can still function without store calls
            }
          } else {
            if (mounted) setIsAuthenticated(false);
          }
        }
      } catch {
        if (mounted) setIsAuthenticated(false);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      login: () => {
        // Always use relative path for auth to stay on the same domain (collector.*)
        // The ingress will route /auth/* to the scraper backend
        const target = '/auth/azure';
        // Fail gracefully if backend is unreachable (avoid endless redirect loop)
        fetch(target, { method: 'GET', mode: 'no-cors' })
          .then(() => {
            window.location.href = target;
          })
          .catch(() => {
            alert('Authentication service is unavailable. Please try again later.');
          });
      },
      logout: async () => {
        // Use relative path for logout as well
        await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
        window.location.href = '/login';
      },
    }),
    [isAuthenticated, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <InnerAuthProvider>{children}</InnerAuthProvider>;
};
